// The Engineering page's period (module 10 U107; Mike, 2026-09-20: "whilst search by week, etc is
// nice. It needs to be by date range as I want to be able to search periods and I also want to look
// back"). A period is a first day and a last day, both inside it, as calendar days in Europe/Paris,
// the zone every figure on the page is cut in. The shortcuts are periods and nothing else, and the
// two arrows move a period back or forward by its own length: a week by a week, a month by a month,
// a quarter by a quarter, anything else by its number of days.
//
// Pure: days in, days out, and "today" is handed in, so the tests fix it.
const TZ = 'Europe/Paris';
const DAY = 86_400_000;

export interface Period { from: string; to: string }
export type ShortcutId = 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'this-quarter' | 'last-quarter';
export const SHORTCUTS: ReadonlyArray<{ id: ShortcutId; label: string }> = [
  { id: 'this-week', label: 'This week' }, { id: 'last-week', label: 'Last week' },
  { id: 'this-month', label: 'This month' }, { id: 'last-month', label: 'Last month' },
  { id: 'this-quarter', label: 'This quarter' }, { id: 'last-quarter', label: 'Last quarter' },
];

export const todayInParis = (nowMs: number): string => new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(nowMs));
const at = (day: string): number => Date.parse(`${day}T12:00:00Z`);
const iso = (t: number): string => new Date(t).toISOString().slice(0, 10);
export const addDays = (day: string, n: number): string => iso(at(day) + n * DAY);
export const isDay = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(at(v)) && iso(at(v)) === v;
const mondayOf = (day: string): string => addDays(day, -((new Date(at(day)).getUTCDay() + 6) % 7));
const firstOfMonth = (day: string): string => `${day.slice(0, 8)}01`;
/** The first day of the month `n` months from the month `day` is in. */
function addMonths(day: string, n: number): string {
  const y = Number(day.slice(0, 4)), m = Number(day.slice(5, 7)) - 1 + n;
  return iso(Date.UTC(y + Math.floor(m / 12), ((m % 12) + 12) % 12, 1, 12));
}
const lastOfMonth = (day: string): string => addDays(addMonths(day, 1), -1);
const min = (a: string, b: string): string => (a < b ? a : b);

export function shortcut(id: ShortcutId, today: string): Period {
  const monday = mondayOf(today), month = firstOfMonth(today);
  const quarter = addMonths(month, -((Number(today.slice(5, 7)) - 1) % 3));
  switch (id) {
    case 'this-week': return { from: monday, to: today };
    case 'last-week': return { from: addDays(monday, -7), to: addDays(monday, -1) };
    case 'this-month': return { from: month, to: today };
    case 'last-month': return { from: addMonths(month, -1), to: addDays(month, -1) };
    case 'this-quarter': return { from: quarter, to: today };
    case 'last-quarter': return { from: addMonths(quarter, -3), to: addDays(quarter, -1) };
  }
}
/** What the page opens on (B1129). Not "this week": on a Monday that is one day, and Mike opened it
 *  on a Monday and saw 11 of the team instead of the 24 who had done something. The last seven days
 *  ending today always spans a working week, whatever day it is read on. It is no shortcut, so no
 *  button is lit and the two dates say what the period is. */
export const defaultPeriod = (today: string): Period => ({ from: addDays(today, -6), to: today });

/** Which shortcut a period is, if it is one: the button that is lit. */
export function shortcutOf(p: Period, today: string): ShortcutId | null {
  return SHORTCUTS.find(({ id }) => { const s = shortcut(id, today); return s.from === p.from && s.to === p.to; })?.id ?? null;
}

/** A period typed backwards is the same period; a last day after today is today. Null for a day that is not one. */
export function tidy(from: string, to: string, today: string): Period | null {
  if (!isDay(from) || !isDay(to)) return null;
  const [a, b] = from <= to ? [from, to] : [to, from];
  const last = min(b, today);
  return { from: min(a, last), to: last };
}

/** What a period is made of, so that the arrows can move it by its own length. A period that stops today is
 *  the unfinished end of a whole one ("this month" on the 20th), and moves as the whole one would. */
function shape(p: Period, today: string): { unit: 'month' | 'week' | 'day'; n: number } {
  const open = p.to === today;
  if (p.from === firstOfMonth(p.from) && (p.to === lastOfMonth(p.to) || open)) {
    const n = (Number(p.to.slice(0, 4)) - Number(p.from.slice(0, 4))) * 12 + Number(p.to.slice(5, 7)) - Number(p.from.slice(5, 7)) + 1;
    return { unit: 'month', n };
  }
  const daysLong = Math.round((at(p.to) - at(p.from)) / DAY) + 1;
  if (p.from === mondayOf(p.from) && (daysLong % 7 === 0 || open)) return { unit: 'week', n: Math.ceil(daysLong / 7) };
  return { unit: 'day', n: daysLong };
}

/** The period before this one (-1) or after it (+1), of the same length. Null when there is nothing after: today is the end. */
export function step(p: Period, dir: -1 | 1, today: string): Period | null {
  const { unit, n } = shape(p, today);
  if (dir === 1 && p.to >= today) return null;
  if (unit === 'month') {
    const from = addMonths(p.from, dir * n);
    return { from, to: min(addDays(addMonths(from, n), -1), today) };
  }
  const length = unit === 'week' ? 7 * n : n;
  const from = addDays(p.from, dir * length);
  return { from, to: min(addDays(from, length - 1), today) };
}

/** The period in an address, `?from=2026-08-01&to=2026-08-31`, or null. */
export function periodFromSearch(search: string, today: string): Period | null {
  const q = new URLSearchParams(search);
  return tidy(q.get('from') ?? '', q.get('to') ?? '', today);
}
export const searchOf = (p: Period): string => `?from=${p.from}&to=${p.to}`;
